import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merge class names, with later Tailwind utilities beating earlier ones.
 *
 * `twMerge` is what makes every component's `className` prop actually work.
 * Without it, `<Button className="px-6">` produces `px-4 px-6` and which one
 * applies depends on the order they happen to occupy in the stylesheet — so an
 * override silently does nothing about half the time. `twMerge` knows the two
 * set the same property and drops the loser. That is the difference between a
 * component that is genuinely extendable and one everybody ends up forking.
 *
 * ── ON CONFIGURING IT ─────────────────────────────────────────────────────
 *
 * We replaced Tailwind's palette and type scale, so the obvious worry is that
 * `twMerge` can no longer tell `text-md` (a size) from `text-tertiary` (a
 * colour) and would treat them as rivals — dropping the size with no error
 * anywhere. That was checked rather than assumed, and it turns out v3's
 * heuristics get every case in this system right: it reads t-shirt steps as
 * sizes and everything else under `text-` as a colour.
 *
 * So there is no custom config here, deliberately. The failure mode is real
 * but this library already handles it, and configuration that earns nothing is
 * just a second thing to keep in sync. `cn.test.ts` pins the behaviour we
 * depend on, so if that ever stops being true it fails loudly rather than
 * quietly restyling components.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
